import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotificationsApi } from "../../hooks";
import { CustomSwitch } from "../../ui/CustomSwitch";
import { LoadingIndicator } from "../MapV2/ui/LoadingIndicator";

interface DataExportSettingsProps {
    company?: number;
    disabled?: boolean;
    onSaveStateChange?: (saved: boolean) => void;
}

export const DataExportSettings = (props: DataExportSettingsProps) => {
    const apiClient = useNotificationsApi();
    const queryClient = useQueryClient();

    const notificationGroupsQuery = useQuery({
        queryKey: ["notificationGroup", props.company],
        queryFn: async () =>
            await apiClient.notificationsGroupsList({
                owner: props.company,
                pageSize: 100,
            }),
    });

    // Notification settings
    const notificationSettingsQuery = useQuery({
        queryKey: ["emissionNotificationSettings", props.company],
        queryFn: async () => {
            const response = await apiClient.notificationsEmissionsList({
                owner: props.company,
                pageSize: 1,
            });
            return response.results[0];
        },
    });

    const saveChangesMutation = useMutation({
        mutationFn: async ({
            field,
            value,
        }: {
            field: string;
            value: string | boolean;
        }) =>
            await apiClient.notificationsEmissionsPartialUpdate({
                id: notificationSettingsQuery.data?.id,
                patchedEmissionNotificationSettingsRequest: {
                    [field]: value,
                },
            }),
        onMutate: () => props.onSaveStateChange?.(false),
        onSuccess: () => {
            props.onSaveStateChange?.(true);
            queryClient.invalidateQueries({
                queryKey: ["emissionNotificationSettings", props.company],
            });
        },
        onError: () => props.onSaveStateChange?.(false),
    });

    const saveChanges = (field: string, value: string | boolean) => {
        if (
            !props.disabled &&
            !saveChangesMutation.isLoading &&
            notificationSettingsQuery.data?.id !== undefined
        ) {
            saveChangesMutation.mutate({ field, value });
        }
    };

    if (
        notificationSettingsQuery.isLoading ||
        notificationGroupsQuery.isLoading
    ) {
        return <LoadingIndicator />;
    }

    return (
        <div className="text-sm">
            <div className="grid grid-cols-[minmax(18rem,1fr)_14rem_18rem] items-center gap-6 px-6 py-4">
                <h3 className="text-sm font-semibold text-slate-900">
                    Emissions CSV export
                </h3>
                <div className="flex items-center justify-center gap-3 text-sm font-medium text-slate-800">
                    <CustomSwitch
                        checked={
                            notificationSettingsQuery.data?.notifyGroupOnReport
                        }
                        onChange={(value) => {
                            saveChanges("notifyGroupOnReport", value);
                        }}
                        size="lg"
                        colorScheme="notification"
                        disabled={
                            props.disabled || saveChangesMutation.isLoading
                        }
                    />
                    {notificationSettingsQuery.data?.notifyGroupOnReport
                        ? "On"
                        : "Off"}
                </div>
                <select
                    aria-label="Data export notification group"
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 disabled:cursor-not-allowed"
                    value={
                        notificationSettingsQuery.data
                            ?.reportNotificationGroup ?? ""
                    }
                    onChange={(event) => {
                        saveChanges(
                            "reportNotificationGroup",
                            event.target.value,
                        );
                    }}
                    disabled={
                        props.disabled ||
                        saveChangesMutation.isLoading ||
                        !notificationSettingsQuery.data?.notifyGroupOnReport
                    }
                >
                    <option value="" disabled>
                        Select a group
                    </option>
                    {(notificationGroupsQuery.data?.results ?? []).map(
                        (group) => (
                            <option key={group.id} value={group.id}>
                                {group.name}
                            </option>
                        ),
                    )}
                </select>
            </div>
        </div>
    );
};
